import * as React from "react"
import * as Yup from "yup"
import Form, { FormElement } from "@modules/form/components/Form"
import { MdArrowForward } from "react-icons/md"
import {
  Button,
  InputConnectedField,
  StyledModalActions,
  Text,
} from "gatsby-interface"
import {
  amazonS3IntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { useConnectS3ToSiteMutation } from "@modules/cdn/amazonS3/queries.generated"
import { CdnIntegrationSharedProps } from "@modules/cdn/shared/types"

type FormValues = {
  accessKeyId: string
  secretAccessKey: string
  bucket: string
  fileKeyPrefix: string
  cloudFrontDistributionId: string
  invalidationPaths: string
}

const validationSchema = Yup.object().shape<FormValues>({
  accessKeyId: Yup.string().required(uiText.messages.validationIsRequired),
  secretAccessKey: Yup.string().required(uiText.messages.validationIsRequired),
  bucket: Yup.string().required(uiText.messages.validationIsRequired),
  fileKeyPrefix: Yup.string(),
  cloudFrontDistributionId: Yup.string(),
  invalidationPaths: Yup.string(),
})

export type AmazonS3IntegrationFormProps = CdnIntegrationSharedProps

export function AmazonS3IntegrationForm({
  siteId,
  integration,
  onSetCDNConfig,
  onSubmitFailed,
  onCancel,
}: AmazonS3IntegrationFormProps) {
  const [mutate] = useConnectS3ToSiteMutation()
  const config = integration?.config

  return (
    <Form<FormValues>
      validationSchema={validationSchema}
      initialValues={{
        accessKeyId: config?.accessKeyId ?? ``,
        secretAccessKey: config?.secretAccessKey ?? ``,
        bucket: config?.bucket ?? ``,
        fileKeyPrefix: config?.fileKeyPrefix ?? ``,
        cloudFrontDistributionId: config?.cloudFrontDistributionId ?? ``,
        invalidationPaths: config?.invalidationPaths ?? ``,
      }}
      onSubmit={values => {
        mutate({
          variables: {
            siteId,
            config: values,
          },
        })
          .then(({ data }) => {
            return onSetCDNConfig({
              success: data?.connectS3ToSite?.success ?? false,
              errorType: data?.connectS3ToSite?.errorType,
            })
          })
          .catch(onSubmitFailed)
      }}
    >
      {({ isSubmitting }) => (
        <FormElement applySpacing noValidate>
          <Text css={{ margin: 0 }}>{text.messages.introText}:</Text>
          <InputConnectedField
            name="accessKeyId"
            label={text.labels.s3AccessKeyID}
            required
          />
          <InputConnectedField
            name="secretAccessKey"
            label={text.labels.s3SecretAccessKey}
            required
          />
          <InputConnectedField
            label={text.labels.s3BucketName}
            name="bucket"
            required
          />
          <InputConnectedField
            label={text.labels.s3FileKeyPrefix}
            name="fileKeyPrefix"
          />
          <InputConnectedField
            label={text.labels.s3CloudFrontDistributionId}
            name="cloudFrontDistributionId"
          />
          <InputConnectedField
            label={text.labels.s3InvalidationPaths}
            name="invalidationPaths"
          />

          <StyledModalActions>
            <Button variant="SECONDARY" type="reset" onClick={onCancel}>
              {uiText.actions.cancel}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              rightIcon={<MdArrowForward />}
            >
              {uiText.actions.save}
            </Button>
          </StyledModalActions>
        </FormElement>
      )}
    </Form>
  )
}
